'use client';

import { cn } from '@fshr/shared-react';
import BevSafeText from '../../bev-safe-text';
import Button from '../../button';
import SortArrows from '../cells/sort-arrows';
import {
	getCellLayoutClassName,
	getColumnStyle,
	getStickyFirstColumnClassName,
} from './cell-layout';
import type { SortableTableColumnMeta, TableCellLayoutContext } from './types';
import type { SortType } from '../types';

interface SortableTableHeaderProps extends TableCellLayoutContext {
	columnMeta: SortableTableColumnMeta[];
	activeSortCol: number | null;
	activeSortDir: 'ascending' | 'descending' | null;
	onSort: (colIndex: number, sortType: SortType) => void;
}

export default function SortableTableHeader({
	columnMeta,
	activeSortCol,
	activeSortDir,
	onSort,
	columnWidths,
	layoutMode,
	isHorizontalOverflow,
	isCompressed,
	hasColumnWidths,
	stickyRowHeader,
	isStickyScrolled,
	rowClassName,
}: SortableTableHeaderProps) {
	return (
		<div role="rowgroup">
			<div
				role="row"
				data-sortable-row
				data-row-role="header"
				className={cn(rowClassName, 'bg-surface-raised-subtle')}
			>
				{columnMeta.map((column, colIndex) => (
					<div
						key={column.id}
						role="columnheader"
						data-sortable-cell
						data-cell-role="header"
						data-column-index={colIndex}
						aria-sort={activeSortCol === colIndex ? (activeSortDir ?? undefined) : undefined}
						style={getColumnStyle(colIndex, columnWidths, layoutMode)}
						className={cn(
							'flex h-[48px] max-h-[48px] items-stretch md:h-[58px] md:max-h-[58px]',
							getCellLayoutClassName(colIndex, isHorizontalOverflow, isCompressed, hasColumnWidths),
							getStickyFirstColumnClassName(
								colIndex,
								stickyRowHeader,
								isHorizontalOverflow,
								isStickyScrolled,
								true
							),
							column.hideOnMobile && 'hidden min-[700px]:flex'
						)}
					>
						{column.sortable ? (
							<Button
								intent="text"
								size="small"
								onClick={() => onSort(colIndex, column.sortType)}
								className="flex h-full w-full min-w-0 items-center justify-start gap-0 !border-none spacing-inline-xs spacing-card-padding-x-xs text-left"
							>
								<span className="truncate text-label text-text-secondary translate-y-[0.2em]">
									<BevSafeText>{column.title}</BevSafeText>
								</span>
								<SortArrows />
							</Button>
						) : (
							<div className="flex h-full w-full min-w-0 items-center spacing-inline-xs spacing-card-padding-x-xs">
								<span className="truncate text-label text-text-secondary">
									<BevSafeText>{column.title}</BevSafeText>
								</span>
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
}
